/**
 * React hook for the hub list endpoints: pagination, page size, debounced search,
 * error state and protection against out-of-order responses.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchList, ListResource, toUserMessage } from "./strapi";
import { PaginationMeta } from "../types/objects";

export interface UsePaginatedListOptions {
    pageSize?: number;
    search?: string;
    sort?: string;
}

export function usePaginatedList<T>(resource: ListResource, options: UsePaginatedListOptions = {}) {
    const [rows, setRows] = useState<T[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(options.pageSize ?? 10);
    const [pagination, setPagination] = useState<PaginationMeta>({ page: 1, pageSize: pageSize, pageCount: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestId = useRef(0);

    const search = options.search ?? "";
    const sort = options.sort;

    // A new search always starts from the first page.
    useEffect(() => {
        setPage(1);
    }, [search]);

    const load = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        try {
            const res = await fetchList<T>(resource, { page, pageSize, search, sort });
            if (id !== requestId.current) return; // a newer request is in flight
            // The page may no longer exist after deletions — jump to the last one.
            const pageCount = res.meta.pagination.pageCount;
            if (page > 1 && pageCount > 0 && page > pageCount) {
                setPage(pageCount);
                return;
            }
            setRows(res.data ?? []);
            setPagination(res.meta.pagination);
        } catch (err) {
            if (id !== requestId.current) return;
            console.error(`[${resource}] Failed to load list`, err);
            setError(toUserMessage(err, "Nepodařilo se načíst seznam ze serveru."));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [resource, page, pageSize, search, sort]);

    useEffect(() => {
        load();
    }, [load]);

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    return { rows, pagination, page, pageSize, loading, error, setPage, setPageSize, reload: load };
}
